import React, { createContext, ReactNode, useContext, forwardRef } from 'react'

type ContextFactoryResult<T, P> = {
  Provider: ProviderComponent<P>
  useValue: () => T
  withProvider: WithProviderComponent<T, P>
}

type ProviderComponent<P> = P extends undefined
  ? ({ children }: { children: ReactNode }) => JSX.Element
  : ({ children, value: P }: { children: ReactNode; value: P }) => JSX.Element

type WithProviderComponent<T, P> = <C extends React.ComponentType<any>>(
  Component: C
) => React.ForwardRefExoticComponent<
  React.PropsWithoutRef<React.ComponentProps<C>> & React.RefAttributes<React.ComponentRef<C>> &
  (P extends undefined ? {} : { value: P })
>

export const createSimpleContext = <T, P = undefined>(
  useValueFactory: (arg: P) => T
): ContextFactoryResult<T, P> => {
  const Context = createContext<T | undefined>(undefined)

  const Provider = (props: { children: ReactNode } & (P extends undefined ? {} : { value: P })) => {
    const { children, value } = props as { children: ReactNode; value?: P }
    const contextValue = useValueFactory(value as P)
    return <Context.Provider value={contextValue}>{children}</Context.Provider>
  }

  const useValue = () => {
    const context = useContext(Context)
    if (!context) {
      throw new Error('useValue must be used within a Provider')
    }
    return context
  }

  const withProvider = <C extends React.ComponentType<any>>(Component: C) => {
    return forwardRef<React.ComponentRef<C>, React.ComponentProps<C> & (P extends undefined ? {} : { value: P })>(
      (props, ref) => {
        const { children, ...rest } = props as { children: ReactNode; value?: P }
        return (
          <Provider {...(rest as any)}>
            <Component ref={ref} {...props} />
          </Provider>
        )
      }
    )
  }

  return { Provider: Provider as ProviderComponent<P>, useValue, withProvider }
}

export default createSimpleContext